import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Document, Node, Block, Inline, Text } from '@contentful/rich-text-types';

import H from '@zola/zola-ui/src/typography/Headings';
import P from '@zola/zola-ui/src/typography/Paragraphs';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';

const getPlainTextFromHeader = (contentNode: Array<Block | Inline | Text>) => {
  return contentNode.reduce((acc, current) => {
    if (current.nodeType !== 'text') return acc;
    return acc + current.value;
  }, '');
};

const splitHeading = (node: Block) => {
  const plainText = getPlainTextFromHeader(node.content);
  return plainText?.split(/\(anchor-([^)]+)\)/);
};

const richTextOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node: Block, children: React.ReactChildren) => {
      const [mainText, id] = splitHeading(node);
      return (
        <H.Title1 presentation="h3" id={id}>
          {id ? mainText : children}
        </H.Title1>
      );
    },
    [BLOCKS.HEADING_2]: (node: Block, children: React.ReactChildren) => {
      const [mainText, id] = splitHeading(node);
      return (
        <H.Title2 presentation="h4" id={id}>
          {id ? mainText : children}
        </H.Title2>
      );
    },
    [BLOCKS.HEADING_3]: (node: Block, children: React.ReactChildren) => {
      const [mainText, id] = splitHeading(node);
      return (
        <H.Title3 presentation="h5" id={id}>
          {id ? mainText : children}
        </H.Title3>
      );
    },
    [BLOCKS.HEADING_4]: (node: Block, children: React.ReactChildren) => {
      const [mainText, id] = splitHeading(node);
      return (
        <H.Title4 presentation="h6" id={id}>
          {id ? mainText : children}
        </H.Title4>
      );
    },
    [BLOCKS.HEADING_5]: (node: Block, children: React.ReactChildren) => {
      const [mainText, id] = splitHeading(node);
      return (
        <H.Title5 presentation="h6" id={id}>
          {id ? mainText : children}
        </H.Title5>
      );
    },
    [BLOCKS.HEADING_6]: (node: Block, children: React.ReactChildren) => {
      const [mainText, id] = splitHeading(node);
      return (
        <H.Title6 presentation="h6" id={id}>
          {id ? mainText : children}
        </H.Title6>
      );
    },
    [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactChildren) => (
      <P.BodyBase>{children}</P.BodyBase>
    ),
    [BLOCKS.TABLE]: (node: Node, children: React.ReactChildren) => (
      <table>
        <tbody>{children}</tbody>
      </table>
    ),
    [BLOCKS.TABLE_ROW]: (node: Node, children: React.ReactChildren) => <tr>{children}</tr>,
    [BLOCKS.TABLE_CELL]: (node: Node, children: React.ReactChildren) => <td>{children}</td>,
    [INLINES.HYPERLINK]: (node: Node, children: React.ReactChildren) => (
      <LinkV2 href={node.data.uri} noTextTransform subtle>
        {children}
      </LinkV2>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node: Node) => {
      // target the contentType of the EMBEDDED_ENTRY to display as you need
      if (node.data.target.sys.contentType.sys.id === 'blogPost') {
        return (
          <a href={`/blog/${node.data.target.fields.slug}`}>{node.data.target.fields.title}</a>
        );
      }
      return null;
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: Node) => {
      // target the contentType of the EMBEDDED_ENTRY to display as you need
      if (node.data.target.sys.contentType.sys.id === 'codeBlock') {
        return (
          <pre>
            <code>{node.data.target.fields.code}</code>
          </pre>
        );
      }

      if (node.data.target.sys.contentType.sys.id === 'videoEmbed') {
        return (
          <iframe
            src={node.data.target.fields.embedUrl}
            height="100%"
            width="100%"
            frameBorder="0"
            scrolling="no"
            title={node.data.target.fields.title}
            allowFullScreen
          />
        );
      }
      return null;
    },

    [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
      // render the EMBEDDED_ASSET as you need
      return (
        <img
          src={`https://${node.data.target.fields.file.url}`}
          height={node.data.target.fields.file.details.image.height}
          width={node.data.target.fields.file.details.image.width}
          alt={node.data.target.fields.description}
        />
      );
    },
  },
};

const mobileRichTextOptions = {
  renderNode: {
    ...richTextOptions.renderNode,
    [INLINES.HYPERLINK]: (node: Node, children: React.ReactChildren) => {
      if (node.data.uri.startsWith('mailto:') || node.data.uri.startsWith('#')) {
        return (
          <LinkV2 href={node.data.uri} noTextTransform subtle>
            {children}
          </LinkV2>
        );
      }

      return (
        <>
          {children} ({node.data.uri})
        </>
      );
    },
  },
};

export const renderRichTextAsHtml = (richText: Document) =>
  // @ts-expect-error Known issue with rich-text-react-renderer and the types in rich-text-types https://github.com/contentful/rich-text/issues/123
  documentToReactComponents(richText, richTextOptions);

export const renderMobileRichTextAsHtml = (richText: Document) =>
  // @ts-expect-error Known issue with rich-text-react-renderer and the types in rich-text-types https://github.com/contentful/rich-text/issues/123
  documentToReactComponents(richText, mobileRichTextOptions);
